import { useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import { Box } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AutocompleteInputForm, DateTimeInputForm, TextInputForm } from '../../../components/common/form'
import { FilterModalContainer } from '../../../components/utilities/FilterModalContainer'
import { countryDictionary } from '../../../components/common/flag/countries'
import { userKycStatusvalues, userStateValues } from '../constants'

const FiltersUsersBusinessForm = ({
  defaultValues,
  setModalOpen,
  handleResetButton,
  handleDispatch,
  setPage,
  uniqueKeys,
  uniqueValues,
  userTags = [],
}) => {
  const { t } = useTranslation(['common'])
  const { handleSubmit, reset, control, watch, setValue } = useForm({
    mode: 'onTouched',
    defaultValues,
  })

  const [fromDate, toDate, userKey] = watch(['fromDate', 'toDate', 'userKey'])

  const handleReset = () => {
    reset()
    handleResetButton()
    setModalOpen(false)
  }

  const filteredValues = useMemo(() => {
    if (!userKey || userTags.length === 0) return uniqueValues || []
    
    const tagItem = userTags.find((tag) => tag.key === userKey)
    return tagItem ? tagItem.values : []
  }, [userKey, userTags, uniqueValues])

  const availableKeys = useMemo(() => {
    if (userTags.length > 0) {
      return userTags.map((tag) => tag.key)
    }
    return uniqueKeys || []
  }, [userTags, uniqueKeys])

  useEffect(() => {
    if (fromDate && !toDate) {
      setValue('toDate', dayjs(fromDate).hour(23).minute(59).second(59))
    } else if (!fromDate && toDate) {
      setValue('fromDate', dayjs(fromDate).hour(0).minute(0).second(0))
    }
  }, [fromDate, toDate, setValue])

  useEffect(() => {
    setValue('userValue', '')
  }, [userKey, setValue])

  return (
    <FilterModalContainer
      setModalOpen={setModalOpen}
      handleResetButton={handleReset}
      setPage={setPage}
      handleDispatch={handleSubmit(handleDispatch)}
    >
      <Box className="modal-box-filters">
        <Box>
          <TextInputForm
            name="userId"
            control={control}
            label={`${t('common:by')} ID:`}
            placeholder={t('common:searchById')}
          />
        </Box>
        <Box>
          <DateTimeInputForm
            name="fromDate"
            timeName="fromTime"
            control={control}
            label={`${t('common:by')} ${t('common:initialDate')}:`}
          />
        </Box>
        <Box>
          <DateTimeInputForm
            name="toDate"
            timeName="toTime"
            control={control}
            label={`${t('common:by')} ${t('common:endDate')}:`}
          />
        </Box>
        <Box>
          <TextInputForm
            name="legalName"
            control={control}
            label={t('common:legalName')}
            placeholder={`${t('common:insert')} ${t('common:legalName')}`}
          />
        </Box>
        <Box>
          <AutocompleteInputForm
            name="countryOfRegistration"
            control={control}
            label={t('common:countryOfRegistration')}
            placeholder={`${t('common:searchBy')} ${t('common:countryOfRegistration')}`}
            options={Object.keys(countryDictionary)}
            getOptionLabel={(option) => countryDictionary[option]}
          />
        </Box>
        <Box>
          <AutocompleteInputForm
            name="userKycStatus"
            control={control}
            label={`${t('common:user')} KYC ${t('common:status')}`}
            placeholder={`${t('common:searchBy')} KYC ${t('common:status')}`}
            options={userKycStatusvalues}
          />
        </Box>
        <Box>
          <AutocompleteInputForm
            name="selectedUserState"
            control={control}
            label={`${t('common:user')} ${t('common:state')}`}
            placeholder={`${t('common:searchBy')} ${t('common:state')}`}
            options={userStateValues}
          />
        </Box>
        {!!availableKeys?.length && (
          <Box className="modal-box">
            <AutocompleteInputForm
              name="userKey"
              control={control}
              label={`${t('common:user')} ${t('common:tagKey')}`}
              options={availableKeys}
              placeholder={`${t('common:searchBy')} ${t('common:tagKey')}`}
            />
          </Box>
        )}

        {!!filteredValues?.length && (
          <Box className="modal-box">
            <AutocompleteInputForm
              name="userValue"
              control={control}
              label={`${t('common:user')} ${t('common:tagValue')}`}
              options={filteredValues}
              placeholder={userKey ? `${t('common:searchBy')} ${t('common:tagValue')}` : t('common:selectKeyFirst')}
              disabled={!userKey}
            />
          </Box>
        )}
      </Box>
    </FilterModalContainer>
  )
}

export default FiltersUsersBusinessForm
